/**
 * Centralized data fetching service for the Unified Dashboard
 *
 * This service fetches all data sources in parallel and returns them together.
 * The merging/transformation logic is handled by the unifiedDashboardStore.
 */

import axios from 'axios';
import { isProd, USE_MOCK_DATA } from '../constants';

// Import mock data for development/fallback
import activeCallsMock from '../mock/getActiveCalls.json';
import mrAppointmentsMock from '../mock/getMediRecordAppointments.json';
import realtimeViewMock from '../mock/getMRRealtimeView.json';
import taskRouterMock from '../mock/getTaskRouterDetails.json';

// API Endpoints
const ENDPOINTS = {
  activeCalls: {
    prod: 'https://med-discrete-data.azurewebsites.net/api/getActiveCalls?code=uj4JpGc2FixqELBtV2ONI8CRTyuYxx7W5dDmDNLWGgkmAzFut-OefQ==',
    uat: 'https://med-discrete-data-uat.azurewebsites.net/api/getActiveCalls?code=eAd-zwgve6xSFibFYHFMp3eDCEpSP9LRSh0zMCUrXbcNAzFuiWWt8w==',
  },
  mrAppointments: {
    prod: 'https://med-discrete-data.azurewebsites.net/api/getMediRecordAppointments?code=hH96zfGZcwZg7caZ44zw6faoAfpbx65mkN1SVLEyvko6AzFuv1HzSg==',
    uat: 'https://med-discrete-data-uat.azurewebsites.net/api/getMediRecordAppointments?code=77cjPmIDJHljOaE2s81dFkoVAn94t5l9C5K1MFhcT0XkAzFulCWCKw==',
  },
  realtimeView: {
    prod: 'https://med-discrete-data.azurewebsites.net/api/getMRRealtimeView?code=CgDGi1bdBAgQ8NNvO0Y3bRdHIkVnLd43okffK6G3UFBFAzFuS6huQA==',
    uat: 'https://med-discrete-data-uat.azurewebsites.net/api/getMRRealtimeView?code=TOXBU9vvqvaA-10lv3Lz0xVxRb2pj_uq_5wFbQdB3J3MAzFulsIhZg==',
  },
  taskRouter: {
    prod: 'https://med-discrete-data.azurewebsites.net/api/getTaskRouterDetails?code=GEFm2qnCUFDKuJi0mcZhowXQWnpJmkqR2TwWB4i1CX3rAzFuyr2r9Q==',
    uat: 'https://med-discrete-data-uat.azurewebsites.net/api/getTaskRouterDetails?code=SLHpVI5TQV1JwX1jpZcJKDSNeefvCwGaj3dTPyFCggsQAzFuczieew==',
  },
  updateTaskPriority: {
    prod: 'https://med-discrete-data.azurewebsites.net/api/updateTaskPriority?code=JMZQzWeW-h7dB4aSsJDXPxABIuuZ5Bv1rxwXQwdROftgAzFu7iVzVQ==',
    uat: 'https://med-discrete-data-uat.azurewebsites.net/api/updateTaskPriority?code=XkkhPtg01-_DHdnteEk9AK8M4UV8KNGiwF1Ykm4rOTjmAzFuQsq82Q==',
  },
};

// Mock data mapping
const MOCK_DATA = {
  activeCalls: activeCallsMock,
  mrAppointments: mrAppointmentsMock,
  realtimeView: realtimeViewMock,
  taskRouter: taskRouterMock,
};

// Environment label for logging
const ENV_LABEL = isProd ? 'PROD' : 'UAT';

/**
 * Generic fetch function for all data sources
 * Reduces code duplication across individual fetch functions
 *
 * @param {string} dataSourceName - Name of the data source (matches keys in ENDPOINTS)
 * @param {boolean} useMock - Whether to use mock data
 * @param {any} defaultValue - Default return value on error ([] for arrays, {} for objects)
 * @returns {Promise<any>}
 */
const fetchDataSource = async (
  dataSourceName,
  useMock,
  defaultValue = [],
  queryParams = {}
) => {
  // Use mock data if requested
  if (useMock) {
    console.log(`[fetchUnifiedData] Using MOCK data for ${dataSourceName}`);
    return MOCK_DATA[dataSourceName];
  }

  // Get appropriate endpoint based on environment
  const url = isProd
    ? ENDPOINTS[dataSourceName].prod
    : ENDPOINTS[dataSourceName].uat;

  // Check if endpoint is configured (not missing SAS code)
  if (!url || url.endsWith('code=')) {
    console.warn(
      `[fetchUnifiedData] ${dataSourceName} endpoint not configured, using mock data`
    );
    return MOCK_DATA[dataSourceName];
  }

  // Fetch from API
  try {
    const res = await axios.get(url, {
      headers: { 'Cache-Control': 'no-cache' },
      params: queryParams,
    });
    console.log(
      `[fetchUnifiedData] Fetched ${dataSourceName} from ${ENV_LABEL}`
    );
    return res.data || defaultValue;
  } catch (err) {
    console.error(`[fetchUnifiedData] Error fetching ${dataSourceName}:`, err);
    return defaultValue;
  }
};

/**
 * Fetch Active Calls from Twilio Task Router
 */
const fetchActiveCalls = async (useMock = USE_MOCK_DATA.activeCalls) => {
  return fetchDataSource('activeCalls', useMock, []);
};

/**
 * Fetch MediRecord Appointments
 */
const fetchMRAppointments = async (useMock = USE_MOCK_DATA.mrAppointments) => {
  return fetchDataSource('mrAppointments', useMock, []);
};

/**
 * Fetch Realtime Waiting Room View
 */
const fetchRealtimeView = async (useMock = USE_MOCK_DATA.realtimeView) => {
  return fetchDataSource('realtimeView', useMock, []);
};

/**
 * Fetch Task Router Details (Queue Statistics)
 */
const fetchTaskRouter = async (
  useMock = USE_MOCK_DATA.taskRouter,
  { optimised = true } = {}
) => {
  return fetchDataSource('taskRouter', useMock, {}, { optimised });
};

/**
 * Fetch all data sources in parallel
 *
 * @param {Object} options - Override mock settings (optional)
 * @param {boolean} options.useMockActiveCalls - Override USE_MOCK_DATA.activeCalls
 * @param {boolean} options.useMockMRAppointments - Override USE_MOCK_DATA.mrAppointments
 * @param {boolean} options.useMockRealtimeView - Override USE_MOCK_DATA.realtimeView
 * @param {boolean} options.useMockTaskRouter - Override USE_MOCK_DATA.taskRouter
 * @param {boolean} options.includeMRAppointments - Whether to fetch MR appointments
 *
 * @returns {Promise<{activeCalls: Array, mrAppointments: Array, realtimeView: Array, taskRouter: Object, fetchedAt: string}>}
 */
export const fetchAllUnifiedData = async (options = {}) => {
  const {
    useMockActiveCalls = USE_MOCK_DATA.activeCalls,
    useMockMRAppointments = USE_MOCK_DATA.mrAppointments,
    useMockRealtimeView = USE_MOCK_DATA.realtimeView,
    useMockTaskRouter = USE_MOCK_DATA.taskRouter,
    includeMRAppointments = false,
    taskRouterOptimised = true,
  } = options;

  console.log(
    '[fetchUnifiedData] Environment:',
    isProd ? 'PRODUCTION' : 'UAT/DEV'
  );
  console.log('[fetchUnifiedData] Mock settings:', {
    activeCalls: useMockActiveCalls,
    mrAppointments: useMockMRAppointments,
    realtimeView: useMockRealtimeView,
    taskRouter: useMockTaskRouter,
    includeMRAppointments,
  });

  const startTime = Date.now();

  // Fetch all sources in parallel
  const mrAppointmentsPromise = includeMRAppointments
    ? fetchMRAppointments(useMockMRAppointments)
    : Promise.resolve([]);

  const [activeCalls, mrAppointments, realtimeView, taskRouter] =
    await Promise.all([
      fetchActiveCalls(useMockActiveCalls),
      mrAppointmentsPromise,
      fetchRealtimeView(useMockRealtimeView),
      fetchTaskRouter(useMockTaskRouter, { optimised: taskRouterOptimised }),
    ]);

  const endTime = Date.now();

  console.log(
    `[fetchUnifiedData] Fetched all data in ${endTime - startTime}ms`,
    {
      activeCalls: activeCalls?.length || 0,
      mrAppointments: mrAppointments?.length || 0,
      realtimeView: realtimeView?.length || 0,
      taskRouterQueues: taskRouter?.queues?.length || 0,
    }
  );

  return {
    activeCalls: activeCalls || [],
    mrAppointments: mrAppointments || [],
    realtimeView: realtimeView || [],
    taskRouter: taskRouter || {},
    fetchedAt: new Date().toISOString(),
  };
};

/**
 * Update a Twilio TaskRouter task's priority
 *
 * @param {string} taskSid - The Task SID to update
 * @param {number} priority - New priority value (non-negative integer)
 * @returns {Promise<{success: boolean, task?: Object, error?: string}>}
 */
export const updateTaskPriority = async (taskSid, priority) => {
  const url = isProd
    ? ENDPOINTS.updateTaskPriority.prod
    : ENDPOINTS.updateTaskPriority.uat;

  console.log(
    `[fetchUnifiedData] Updating task ${taskSid} priority to ${priority} (${ENV_LABEL})`
  );

  const res = await axios.post(url, { taskSid, priority });
  return res.data;
};

// Export individual fetchers for flexibility
export {
  fetchActiveCalls,
  fetchMRAppointments,
  fetchRealtimeView,
  fetchTaskRouter,
};
